import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiCaretDown, BiCaretUp } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LanguageMapper,
  MultiLanguageUrl
} from "../../constants/multiLanguage.constant";

// A memoized Image component with caching
const FlagImage = React.memo(({ src }: { src: string }) => {
  return <img className="tw-w-6 tw-h-6 tw-rounded-full" src={src} alt="flag" />;
});

FlagImage.displayName = "FlagImage";

const LanguageChangeDropdown = ({
  theme,
  size = "lg"
}: {
  theme?: string;
  size?: "lg" | "sm";
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const lngFlagMapper = useMemo(
    () => ({
      "en-US": "https://cdn.1cdn.app/application/UMD/24041507435389_US.png",
      "fr-FR": "https://cdn.1cdn.app/application/UMD/24041507435387_France.png"
    }),
    []
  );

  const flag = useMemo(() => {
    return lngFlagMapper[MultiLanguageUrl[i18n.language || "en-US"]];
  }, [i18n.language]);

  const [cachedFlagUrl, setCachedFlagUrl] = useState<string | null>(null);

  useEffect(() => {
    const cachedFlag = localStorage.getItem(`flag-${i18n.language}`);
    if (cachedFlag) {
      setCachedFlagUrl(cachedFlag);
    } else {
      setCachedFlagUrl(flag);
      localStorage.setItem(`flag-${i18n.language}`, flag);
    }
  }, [flag, i18n.language]);

  const [toggle, setToggle] = useState(true);

  // Handle toggling caret visibility
  const handleToggle = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      setToggle((prev) => !prev);
    },
    []
  );

  const selectHandler = (l: string) => {
    const url = new URL(window.location.href);
    const params = url.search || "";
    const from = url.pathname.replace(
      MultiLanguageUrl[i18n.language],
      MultiLanguageUrl[l]
    );

    i18n.changeLanguage(l);
    navigate(from + params, { replace: true });
  };

  // Memoize the dropdown items
  const dropdownItems = useMemo(() => {
    return Object.keys(i18n.services.resourceStore.data).map((l) => (
      <Dropdown.Item key={l} onClick={() => selectHandler(l)}>
        {LanguageMapper[l]}
      </Dropdown.Item>
    ));
  }, [i18n.services.resourceStore.data]);

  const CustomToggle = React.forwardRef(
    ({ children, onClick }: { children: any; onClick: any }, ref) => (
      <span
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
          handleToggle(e); // Handle toggle state change
        }}
        onKeyDown={(e) => {
          e.preventDefault();
          onClick(e);
          handleToggle(e); // Handle toggle state change on keydown
        }}
        role="button"
        tabIndex={-1}
      >
        {children}
      </span>
    )
  );

  CustomToggle.displayName = "CustomToggle";

  return (
    <div
      className={`tw-w-full tw-flex ${
        size === "sm"
          ? "tw-py-2 tw-px-[6px] tw-justify-center"
          : "tw-py-[10px] tw-px-2 tw-justify-between"
      } tw-gap-2`}
    >
      {size !== "sm" && (
        <p className="tw-m-0 tw-text-zinc-500 tw-text-sm tw-font-normal">
          Languages
        </p>
      )}
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle}>
          <div className="tw-flex tw-justify-center tw-items-center tw-gap-1">
            {/* Use the cached flag URL or fallback to the default flag */}
            {cachedFlagUrl && <FlagImage src={cachedFlagUrl} />}
            {toggle ? (
              <BiCaretUp
                size={16}
                className={`${
                  theme === "light" ? "tw-text-black" : "tw-text-indigo-50"
                }`}
              />
            ) : (
              <BiCaretDown
                size={16}
                className={`${
                  theme === "light" ? "tw-text-black" : "tw-text-indigo-50"
                }`}
              />
            )}
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className={`tw-bottom-7 ${
            size === "sm" ? "tw-left-3" : "tw-right-3"
          }`}
        >
          {dropdownItems}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default React.memo(LanguageChangeDropdown);
